import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { inr, inrCompact, num, fmtDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { KpiCard } from "@/components/KpiCard";
import { ArrowLeft, Building2, Users, Package, IndianRupee, Phone, Mail, MapPin, Calendar, Pencil, BadgeCheck } from "lucide-react";
import { PartnerCertificateCard } from "@/components/certs/PartnerCertificateCard";
import { EditFranchiseDialog } from "@/components/EditFranchiseDialog";
import { useApp } from "@/context/AppContext";

export default function FranchiseProfile() {
  const { id } = useParams();
  const [f, setF] = useState(null);
  const nav = useNavigate();
  const { can } = useApp();
  const [editOpen, setEditOpen] = useState(false);
  const [rev, setRev] = useState(0);
  const load = () => api.get(`/franchise/${id}`).then(({ data }) => setF(data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f) return <div className="text-muted-foreground">Loading…</div>;
  const s = f.stats || {};

  return (
    <div>
      <button onClick={() => nav(-1)} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-blue-500 text-white"><Building2 className="h-7 w-7" /></div>
          <div>
            <div className="flex flex-wrap items-center gap-3"><h1 className="font-head text-2xl font-bold">{f.name}</h1><StatusBadge status={f.status} />
              {f.verification_status === "Verified" ? <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-500" data-testid="fp-verification"><BadgeCheck className="h-3.5 w-3.5" />Verified</span>
                : <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-500" data-testid="fp-verification">Verification Pending</span>}</div>
            <p className="mt-1 text-sm text-muted-foreground font-mono">{f.referral_number}</p>
            <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
              {f.contact && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{f.contact}</span>}
              {f.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{f.email}</span>}
              <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{f.city}{f.state ? `, ${f.state}` : ""}</span>
              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Joined {fmtDate(f.joining_date)}</span>
              {f.contact_person && <span className="flex items-center gap-1" data-testid="fp-contact-person"><Users className="h-3 w-3" />{f.contact_person}</span>}
            </p>
          </div>
        </div>
        {can("fr.edit") && <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="fp-edit-btn"><Pencil className="mr-1.5 h-4 w-4" /> Edit Franchise</Button>}
        </div>
      </div>
      <PartnerCertificateCard key={rev} kind="franchise" partnerId={f.id} />
      <EditFranchiseDialog franchise={f} open={editOpen} onClose={() => setEditOpen(false)} onSaved={() => { load(); setRev((r) => r + 1); }} />

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="Delivery Partners" value={num(s.total_dp)} icon={Users} accent="primary" sub={`${s.active_dp} active`} />
        <KpiCard label="Total Orders" value={num(s.total_orders)} icon={Package} accent="cyan" sub={`${num(s.today_orders)} today`} />
        <KpiCard label="Month Orders" value={num(s.month_orders)} icon={Package} accent="violet" sub={`${num(s.week_orders)} this week`} />
        <KpiCard label="Total Earnings" value={inrCompact(s.total_earning)} icon={IndianRupee} accent="emerald" sub={`${inr(s.month_earning)} this month`} />
      </div>

      <Tabs defaultValue="partners" className="mt-6">
        <TabsList>
          <TabsTrigger value="partners" data-testid="fp-tab-partners">Delivery Partners</TabsTrigger>
          <TabsTrigger value="performance" data-testid="fp-tab-performance">DP Performance</TabsTrigger>
          <TabsTrigger value="daywise" data-testid="fp-tab-daywise">Date-wise Earnings</TabsTrigger>
        </TabsList>

        <TabsContent value="partners" className="mt-4">
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>DP</TableHead><TableHead>FE ID</TableHead><TableHead>Location</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>{f.dp_list.map((d) => (
                <TableRow key={d.id} className="cursor-pointer" onClick={() => nav(`/dp/${d.id}`)} data-testid={`fp-dp-${d.fe_id}`}><TableCell className="font-semibold">{d.name}</TableCell><TableCell className="font-mono text-xs">{d.fe_id}</TableCell><TableCell>{d.location}</TableCell><TableCell><StatusBadge status={d.status} /></TableCell></TableRow>
              ))}</TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="performance" className="mt-4">
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>DP</TableHead><TableHead>FE ID</TableHead><TableHead className="text-right">Orders</TableHead><TableHead className="text-right">Franchise Earning</TableHead></TableRow></TableHeader>
              <TableBody>{f.partners.map((p) => (
                <TableRow key={p.dp_id} className="cursor-pointer" onClick={() => nav(`/dp/${p.dp_id}`)}><TableCell className="font-semibold">{p.name}</TableCell><TableCell className="font-mono text-xs">{p.fe_id}</TableCell><TableCell className="text-right tabular">{num(p.orders)}</TableCell><TableCell className="text-right tabular font-semibold text-emerald-500">{inr(p.earning)}</TableCell></TableRow>
              ))}</TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="daywise" className="mt-4">
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Date</TableHead><TableHead className="text-right">Orders</TableHead><TableHead className="text-right">Franchise Earning</TableHead></TableRow></TableHeader>
              <TableBody>{f.daywise.map((d) => (
                <TableRow key={d.date}><TableCell>{fmtDate(d.date)}</TableCell><TableCell className="text-right tabular">{num(d.orders)}</TableCell><TableCell className="text-right tabular font-semibold text-emerald-500">{inr(d.earning)}</TableCell></TableRow>
              ))}</TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
